import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import './PrivateReplyModal.css';

/**
 * PrivateReplyModal — admin-only modal that sends a Messenger/Instagram DM
 * in response to a public comment via the Private Reply endpoint.
 *
 * Props:
 *   platform           — 'facebook' | 'instagram'
 *   commentId          — Meta comment ID (string; digits + underscores)
 *   originalComment    — read-only comment text shown in the modal
 *   initialReply       — (optional) pre-filled suggestion text
 *   onClose            — close callback
 *   onSent             — (optional) callback after a successful send
 */
function PrivateReplyModal({
  platform,
  commentId,
  originalComment,
  initialReply = '',
  onClose,
  onSent,
}) {
  const { t } = useTranslation();
  const [replyText, setReplyText] = useState(initialReply);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const mapErrorToToast = (detail, status) => {
    const lower = String(detail || '').toLowerCase();
    if (status === 429) return t('privateReply.toast.error.generic');
    if (lower.includes('window') || lower.includes('expired')) {
      return t('privateReply.toast.error.windowExpired');
    }
    if (lower.includes('already')) return t('privateReply.toast.error.alreadySent');
    if (lower.includes('permission') || lower.includes('scope')) {
      return t('privateReply.toast.error.permission');
    }
    return t('privateReply.toast.error.generic');
  };

  const handleSend = async () => {
    const text = (replyText || '').trim();
    if (!text) return;
    setSending(true);
    setFeedback(null);
    try {
      const endpoint =
        platform === 'facebook'
          ? '/api/v1/private-reply/facebook'
          : '/api/v1/private-reply/instagram';
      const response = await apiClient.post(endpoint, {
        comment_id: commentId,
        text,
      });

      const status = response?.data?.status;
      if (status === 'sent') {
        setFeedback({ type: 'success', message: t('privateReply.toast.success') });
        if (onSent) onSent(response.data);
      } else if (status === 'duplicate') {
        setFeedback({
          type: 'error',
          message: t('privateReply.toast.error.alreadySent'),
        });
      } else if (status === 'expired') {
        setFeedback({
          type: 'error',
          message: t('privateReply.toast.error.windowExpired'),
        });
      } else if (status === 'skipped_disabled') {
        setFeedback({
          type: 'error',
          message: t('privateReply.toast.error.generic'),
        });
      } else {
        setFeedback({
          type: 'error',
          message:
            response?.data?.error_message ||
            t('privateReply.toast.error.generic'),
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setFeedback({ type: 'error', message: mapErrorToToast(detail, status) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="prm-header">
          <h3>{t('privateReply.modal.title')}</h3>
          <button className="prm-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="prm-body">
          <label className="prm-label">
            {t('privateReply.modal.originalComment')}
          </label>
          <div className="prm-original-comment">{originalComment}</div>

          <label className="prm-label" htmlFor="prm-textarea">
            {t('privateReply.modal.replyLabel')}
          </label>
          <textarea
            id="prm-textarea"
            className="prm-textarea"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={4}
            maxLength={2000}
          />

          {feedback && (
            <div className={`prm-feedback prm-feedback-${feedback.type}`}>
              {feedback.message}
            </div>
          )}
        </div>

        <div className="prm-footer">
          <button className="prm-btn prm-btn-cancel" onClick={onClose}>
            {t('privateReply.modal.cancel')}
          </button>
          <button
            className="prm-btn prm-btn-send"
            onClick={handleSend}
            disabled={sending || !replyText.trim()}
          >
            {sending ? '…' : t('privateReply.modal.send')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrivateReplyModal;
